(() => {
  // KIK FAQ accordion component
  if (customElements.get('kik-faq-accordion')) return;

  class KikFaqAccordion extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.resizeObserver = null;
      this.prefersReducedMotion = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.allowMultiple = this.hasAttribute('data-allow-multiple');
      this.openFirst = this.hasAttribute('data-open-first');

      this.prefersReducedMotion =
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;

      this.setupResizeObserver();
      this.initAccordionItems();
    }

    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const panelInner = entry.target;
          const panel = panelInner.closest('.kik-faq-accordion__panel');
          const trigger = panel?.previousElementSibling;

          if (!panel || !trigger || trigger.getAttribute('aria-expanded') !== 'true') {
            return;
          }

          panel.style.maxHeight = this.prefersReducedMotion
            ? 'none'
            : `${panelInner.offsetHeight}px`;
        });
      });
    }

    initAccordionItems() {
      const items = this.querySelectorAll('kik-faq-item');

      items.forEach((item, index) => {
        const trigger = item.querySelector('.kik-faq-accordion__trigger');
        const panel = item.querySelector('.kik-faq-accordion__panel');
        const panelInner = panel?.querySelector('.kik-faq-accordion__panel-inner');

        if (!trigger || !panel || !panelInner) return;

        if (this.resizeObserver) {
          this.resizeObserver.observe(panelInner);
        }

        // Open first item if requested
        if (index === 0 && this.openFirst) {
          requestAnimationFrame(() =>
            this.openAccordionItem(trigger, panel, panelInner, false),
          );
        }

        trigger.addEventListener('click', () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (!this.allowMultiple) {
            this.closeAllExcept(trigger);
          }

          if (isOpen) {
            this.closeAccordionItem(trigger, panel, panelInner);
          } else {
            this.openAccordionItem(trigger, panel, panelInner, true);
          }
        });
      });
    }

    closeAllExcept(activeTrigger) {
      const triggers = this.querySelectorAll('.kik-faq-accordion__trigger');

      triggers.forEach((trigger) => {
        if (trigger === activeTrigger) return;
        if (trigger.getAttribute('aria-expanded') !== 'true') return;

        const panel = trigger.nextElementSibling;
        const panelInner = panel?.querySelector('.kik-faq-accordion__panel-inner');

        if (panel && panelInner) {
          this.closeAccordionItem(trigger, panel, panelInner);
        }
      });
    }

    openAccordionItem(trigger, panel, panelInner, animate) {
      const item = trigger.closest('kik-faq-item');

      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');

      if (item) {
        item.setAttribute('data-state', 'open');
      }

      if (this.prefersReducedMotion || !animate) {
        panel.style.maxHeight = 'none';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const targetHeight = panelInner.offsetHeight;
      panel.setAttribute('data-animate', 'true');
      panel.style.maxHeight = `${targetHeight}px`;
    }

    closeAccordionItem(trigger, panel, panelInner) {
      const item = trigger.closest('kik-faq-item');

      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      if (item) {
        item.setAttribute('data-state', 'closed');
      }

      if (this.prefersReducedMotion) {
        panel.style.maxHeight = '0';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const isAnimating = panel.getAttribute('data-animate') === 'true';
      const currentHeight = panelInner.offsetHeight;

      panel.style.maxHeight = `${currentHeight}px`;

      if (!isAnimating) {
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0';
        });
      } else {
        panel.style.maxHeight = '0';
      }

      panel.setAttribute('data-animate', 'true');
    }
  }

  customElements.define('kik-faq-accordion', KikFaqAccordion);

  if (!customElements.get('kik-faq-item')) {
    class KikFaqItem extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
      }
    }

    customElements.define('kik-faq-item', KikFaqItem);
  }


 class KikMarqueeText extends HTMLElement {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(this._calculateDuration.bind(this)).observe(this);
      }
    }
    _calculateDuration(entries) {
      const scrollingSpeed = parseInt(this.getAttribute("scrolling-speed") || 5), contentWidth = entries[0].contentRect.width, slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);
      this.style.setProperty("--marquee-animation-duration", `${(scrollingSpeed * slowFactor * entries[0].target.querySelector("div").clientWidth / contentWidth).toFixed(3)}s`);
    }
  };
  if (!window.customElements.get("kik-marquee-text")) {
    window.customElements.define("kik-marquee-text", KikMarqueeText);
  }

  class KikContactLink extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'link');
      }

      if (!this.hasAttribute('tabindex')) {
        this.tabIndex = 0;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
    }

    get buttonUrl() {
      return (this.dataset.buttonUrl || '').trim();
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.handleClick(event);
    }

    handleClick(event) {
      const url = this.buttonUrl;

      if (!url) return;

      if (url.startsWith('#') || url.startsWith('.')) {
        event.preventDefault();

        const clicked = this.triggerSelectorTarget(url);

        if (!clicked) {
          console.warn(`kik-contact-link: target not found for selector "${url}"`);
        }

        return;
      }

      if (url.startsWith('tel:') || url.startsWith('mailto:')) {
        window.location.href = url;
        return;
      }

      window.location.href = url;
    }

    triggerSelectorTarget(selector) {
      const clickWithin = (root) => {
        const target = root?.querySelector(selector);
        if (!target) return false;
        target.click();
        return true;
      };

      if (clickWithin(document)) return true;

      const iframes = document.querySelectorAll('iframe');

      for (const frame of iframes) {
        try {
          const frameDoc = frame.contentDocument || frame.contentWindow?.document;
          if (!frameDoc) continue;
          if (clickWithin(frameDoc)) return true;
        } catch (error) {
          // Ignore cross-origin frames we cannot access
          continue;
        }
      }

      return false;
    }
  }

  if (!customElements.get('kik-contact-link')) {
    customElements.define('kik-contact-link', KikContactLink);
  }

  // Kik countdown timer -----------------------------------

  function kikCountdownPad(value) {
    value = Math.floor(value);
    if (value < 0) value = 0;
    return value < 10 ? '0' + value : String(value);
  }

  class KikCountdown extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._interval = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._hoursEl = this.querySelector('[data-kik-countdown-hours]');
      this._minutesEl = this.querySelector('[data-kik-countdown-minutes]');
      this._secondsEl = this.querySelector('[data-kik-countdown-seconds]');

      if (!this._hoursEl || !this._minutesEl || !this._secondsEl) return;

      this._startCountdown();
    }

    disconnectedCallback() {
      this._clearInterval();
    }

    static get observedAttributes() {
      return [
        'data-kik-countdown-duration-hours',
        'data-kik-countdown-duration-minutes',
        'data-kik-countdown-duration-seconds',
      ];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._initialized) return;
      if (oldValue === newValue) return;
      this._startCountdown();
    }

    _clearInterval() {
      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }
    }

    _getDurationSeconds() {
      const hoursAttr = this.getAttribute('data-kik-countdown-duration-hours') || '0';
      const minutesAttr = this.getAttribute('data-kik-countdown-duration-minutes') || '0';
      const secondsAttr = this.getAttribute('data-kik-countdown-duration-seconds') || '0';

      let hours = parseInt(hoursAttr, 10);
      let minutes = parseInt(minutesAttr, 10);
      let seconds = parseInt(secondsAttr, 10);

      if (Number.isNaN(hours)) hours = 0;
      if (Number.isNaN(minutes)) minutes = 0;
      if (Number.isNaN(seconds)) seconds = 0;

      if (hours < 0) hours = 0;
      if (minutes < 0) minutes = 0;
      if (seconds < 0) seconds = 0;

      if (minutes > 59) minutes = 59;
      if (seconds > 59) seconds = 59;

      return hours * 3600 + minutes * 60 + seconds;
    }

    _setTime(hours, minutes, seconds) {
      this._hoursEl.textContent = kikCountdownPad(hours);
      this._minutesEl.textContent = kikCountdownPad(minutes);
      this._secondsEl.textContent = kikCountdownPad(seconds);
    }

    _startCountdown() {
      this._clearInterval();

      const totalSecondsDuration = this._getDurationSeconds();

      if (totalSecondsDuration <= 0) {
        this._setTime(0, 0, 0);
        return;
      }

      const endTime = Date.now() + totalSecondsDuration * 1000;

      const update = () => {
        const now = Date.now();
        const diff = endTime - now;

        if (diff <= 0) {
          this._setTime(0, 0, 0);
          this._clearInterval();
          return;
        }

        const totalSeconds = Math.floor(diff / 1000);
        let hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 99) {
          hours = 99;
        }

        this._setTime(hours, minutes, seconds);
      };

      update();
      this._interval = window.setInterval(update, 1000);
    }
  }

  if (!customElements.get('kik-countdown')) {
    customElements.define('kik-countdown', KikCountdown);
  }

  // Kik wrappers / zoom -----------------------------------

  // Lightweight wrapper to avoid overriding base product-gallery
  (() => {
    const BaseProductGallery = customElements.get('product-gallery') || HTMLElement;
    if (!customElements.get('kik-product-gallery')) {
      class KikProductGallery extends BaseProductGallery {}
      customElements.define('kik-product-gallery', KikProductGallery);
    }
  })();

  const loadSwiperForKik = (() => {
    let promise = null;
    return () => {
      if (window.Swiper) return Promise.resolve();
      if (promise) return promise;
      promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('kik-gallery-zoom-button: failed to load Swiper'));
        document.head.appendChild(script);
      });
      return promise;
    };
  })();

  class KikGalleryZoomButton extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this._trigger = this.querySelector('button') || this;
      this._trigger.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this._trigger?.removeEventListener('click', this._onClick);
      this._teardownOverlay();
    }

    async _onClick(event) {
      console.log('onClick');
      event?.preventDefault?.();
      event?.stopPropagation?.();
      await this._openOverlay();
    }

    async _openOverlay() {
      const container = this.closest('.kik-gallery-zoom') || this.closest('kik-gallery-zoom') || document;
      const dots = container.querySelector('.product-gallery__thumbnail-list');
      const buttons = Array.from(dots?.querySelectorAll('button:not([hidden])') || []);

      // Collect media items from visible gallery slides to preserve type (image/video)
      const mediaNodes = Array.from(
        container.querySelectorAll('.product-gallery__media:not([hidden])'),
      );

      const items = mediaNodes
        .map((node, index) => {
          const type = node.getAttribute('data-media-type') || 'image';
          const thumbImg = buttons[index]?.querySelector('img');
          if (type === 'image') {
            const img = node.querySelector('img');
            if (!img) return null;
            return {
              type: 'image',
              src: img.currentSrc || img.src,
              srcset: img.srcset,
              sizes: img.sizes,
              alt: img.alt || '',
              thumb: thumbImg,
            };
          }

          if (type === 'video' || type === 'external_video') {
            const videoEl = node.querySelector('video');
            const iframeEl = node.querySelector('iframe');
            let html = '';
            if (videoEl) {
              const clone = videoEl.cloneNode(true);
              clone.removeAttribute('autoplay');
              clone.removeAttribute('loop');
              clone.removeAttribute('muted');
              clone.setAttribute('controls', 'true');
              clone.currentTime = 0;
              html = clone.outerHTML;
            } else if (iframeEl) {
              const clone = iframeEl.cloneNode(true);
              html = clone.outerHTML;
            }
            return {
              type: 'video',
              html,
              thumb: thumbImg,
              alt: thumbImg?.alt || '',
            };
          }
          return null;
        })
        .filter(Boolean);

      if (!items.length) {
        console.warn('kik-gallery-zoom-button: no media found for zoom');
        return;
      }
      if (!window.Swiper) {
        try {
          await loadSwiperForKik();
        } catch (err) {
          console.warn(err?.message || 'kik-gallery-zoom-button: Swiper not available');
          return;
        }
      }

      this._ensureOverlay();
      this._renderSlides(items);
      const initialIndex = Math.max(
        0,
        buttons.findIndex((btn) => btn.getAttribute('aria-current') === 'true'),
      );
      this._initSwipers(initialIndex);
      this._showOverlay();
    }

    _ensureOverlay() {
      if (this._overlay) return;
      const overlay = document.createElement('div');
      overlay.className = 'kik-zoom-overlay';
      overlay.innerHTML = `
        <div class="kik-zoom" role="dialog" aria-modal="true" aria-label="Gallery">
          <button type="button" class="kik-zoom__close" aria-label="Close gallery"><span aria-hidden="true">&times;</span></button>
          <div class="kik-zoom__stage">
            <button type="button" class="kik-zoom__arrow kik-zoom__arrow--prev" aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M15 5 8 12l7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="swiper kik-zoom__swiper">
              <div class="swiper-wrapper"></div>
            </div>
            <button type="button" class="kik-zoom__arrow kik-zoom__arrow--next" aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="swiper kik-zoom__thumbs">
            <div class="swiper-wrapper"></div>
          </div>
        </div>
      `;

      overlay.addEventListener('click', this._onOverlayClick);
      document.body.appendChild(overlay);

      this._overlay = overlay;
      this._mainWrapper = overlay.querySelector('.kik-zoom__swiper .swiper-wrapper');
      this._thumbWrapper = overlay.querySelector('.kik-zoom__thumbs .swiper-wrapper');
      this._closeBtn = overlay.querySelector('.kik-zoom__close');
      this._prevBtn = overlay.querySelector('.kik-zoom__arrow--prev');
      this._nextBtn = overlay.querySelector('.kik-zoom__arrow--next');

      this._closeBtn?.addEventListener('click', () => this._hideOverlay());
    }

    _renderSlides(items) {
      if (!this._mainWrapper || !this._thumbWrapper) return;
      this._mainWrapper.innerHTML = '';
      this._thumbWrapper.innerHTML = '';

      items.forEach((item) => {
        const main = document.createElement('div');
        main.className = 'swiper-slide';
        if (item.type === 'video' && item.html) {
          main.innerHTML = `<div class="kik-zoom__video">${item.html}</div>`;
        } else {
          main.innerHTML = `<img src="${item.src}" ${item.srcset ? `srcset="${item.srcset}"` : ''} ${
            item.sizes ? `sizes="${item.sizes}"` : ''
          } alt="${this._escape(item.alt)}" />`;
        }
        this._mainWrapper.appendChild(main);

        const thumb = document.createElement('div');
        thumb.className = 'swiper-slide';
        const thumbImg = item.thumb;
        const thumbSrc = thumbImg?.currentSrc || thumbImg?.src || item.src || '';
        const thumbAlt = thumbImg?.alt || item.alt || '';
        const badge = item.type === 'video' ? '<span class="kik-zoom__thumb-badge" aria-hidden="true">▶</span>' : '';
        thumb.innerHTML = `<img src="${thumbSrc}" alt="${this._escape(thumbAlt)}" />${badge}`;
        this._thumbWrapper.appendChild(thumb);
      });
    }

    _initSwipers(initialIndex) {
      this._thumbSwiper?.destroy(true, true);
      this._mainSwiper?.destroy(true, true);

      this._thumbSwiper = new window.Swiper(this._thumbWrapper.parentElement, {
        spaceBetween: 5,
        slidesPerView: 'auto',
        freeMode: false,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centeredSlides: false,
      });

      this._mainSwiper = new window.Swiper(this._mainWrapper.parentElement, {
        spaceBetween: 10,
        slidesPerView: 1,
        initialSlide: initialIndex,
        navigation: {
          nextEl: this._nextBtn,
          prevEl: this._prevBtn,
        },
        thumbs: {
          swiper: this._thumbSwiper,
        },
        allowTouchMove: true,
        on: {
          afterInit: (swiper) => {
            // ensure navigation buttons are bound even if rendered outside Swiper
            swiper.navigation?.update();
            this._scrollThumbIntoView(swiper.activeIndex);
          },
          slideChange: (swiper) => {
            this._scrollThumbIntoView(swiper.activeIndex);
          },
        },
      });

      const multiple = (this._mainSwiper.slides?.length || 0) > 1;
      this._prevBtn?.toggleAttribute('hidden', !multiple);
      this._nextBtn?.toggleAttribute('hidden', !multiple);
    }

    _showOverlay() {
      if (!this._overlay) return;
      this._overlay.classList.add('is-visible');
      document.body.classList.add('kik-zoom-open');
      document.documentElement.classList.add('kik-zoom-open');
      document.addEventListener('keydown', this._onKeydown);
    }

    _hideOverlay() {
      if (!this._overlay) return;
      this._overlay.classList.remove('is-visible');
      document.body.classList.remove('kik-zoom-open');
      document.documentElement.classList.remove('kik-zoom-open');
      document.removeEventListener('keydown', this._onKeydown);
    }

    _teardownOverlay() {
      document.removeEventListener('keydown', this._onKeydown);
      if (this._overlay?.parentElement) {
        this._overlay.removeEventListener('click', this._onOverlayClick);
        this._overlay.parentElement.removeChild(this._overlay);
      }
      this._mainSwiper?.destroy(true, true);
      this._thumbSwiper?.destroy(true, true);
      this._overlay = null;
      this._mainWrapper = null;
      this._thumbWrapper = null;
      this._mainSwiper = null;
      this._thumbSwiper = null;
    }

    _onOverlayClick(event) {
      if (event.target === this._overlay) {
        this._hideOverlay();
      }
    }

    _onKeydown(event) {
      if (!this._overlay?.classList.contains('is-visible')) return;
      if (event.key === 'Escape') {
        this._hideOverlay();
      } else if (event.key === 'ArrowRight') {
        this._mainSwiper?.slideNext();
      } else if (event.key === 'ArrowLeft') {
        this._mainSwiper?.slidePrev();
      }
    }

    _escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _scrollThumbIntoView(index) {
      if (!this._thumbSwiper) return;
      const slides = this._thumbSwiper.slides || [];
      const slide = slides[index];
      if (!slide) return;

      const slideWidth = slide.clientWidth || 80;
      const visibleCount = Math.max(1, Math.floor(this._thumbSwiper.width / slideWidth));
      const target = Math.max(
        0,
        Math.min(index - Math.floor((visibleCount - 1) / 2), slides.length - visibleCount),
      );

      this._thumbSwiper.slideTo(target, 300, false);
    }
  }

  if (!customElements.get('kik-gallery-zoom-button')) {
    customElements.define('kik-gallery-zoom-button', KikGalleryZoomButton);
  }
})();
